import type { Namespace } from '../types';

// DE drafted — queued for native review. Site incident console (R-125): the
// INC engine at the site tier — computed priority, SLA clocks, Resolved ≠ Closed.
export const siteItsm = {
  pageTitle: { de: 'Maintenance', en: 'Maintenance' },
  pageDescLocal: {
    de: 'Incidents dieses Standorts — Priorität berechnet, SLA-Uhren, Gelöst ≠ Geschlossen.',
    en: 'This site’s incidents — computed priority, SLA clocks, Resolved ≠ Closed.',
  },
  pageDescCloud: {
    de: 'Flottenweite Incident-Übersicht (nur Lesen) — Betrieb passiert an den Standorten.',
    en: 'Fleet-wide incident view (read-only) — operation happens at the sites.',
  },
  colSite: { de: 'Standort', en: 'Site' },
  colUpdated: { de: 'Aktualisiert', en: 'Updated' },
  statOpen: { de: 'Offene Incidents', en: 'Open incidents' },
  statP1: { de: 'P1 offen', en: 'P1 open' },
  statEscalated: { de: 'SLA eskaliert', en: 'SLA escalated' },
  showAll: { de: 'Alle anzeigen', en: 'Show all' },
  showOpen: { de: 'Nur offene', en: 'Open only' },

  colNumber: { de: 'Nummer', en: 'Number' },
  colTitle: { de: 'Titel', en: 'Title' },
  // R-235 follow-up: the list's Incident column merges number+title into one
  // stacked cell — a scoped key rather than reusing `colStatus`/`colCaller`
  // (shared across TechnicianMobile and the Maintenance tab strip, where
  // "Status"/"Caller" are still correct) so this table's own header wording
  // does not leak into screens that never asked for it.
  colIncident: { de: 'Incident', en: 'Incident' },
  // R-235 follow-up: the record's tab strip. The other four tabs reuse each
  // card's own existing title key (`slaPanelTitle`/`linkedChangesTitle`/
  // `workLogTitle`/`knowledgeTitle`) rather than duplicating the translation.
  tabOverview: { de: 'Übersicht', en: 'Overview' },
  tabAttachments: { de: 'Anhänge', en: 'Attachments' },
  colPriority: { de: 'Priorität', en: 'Priority' },
  colStatus: { de: 'Status', en: 'Status' },
  colIncidentState: { de: 'Zustand', en: 'State' },
  colCaller: { de: 'Melder', en: 'Caller' },
  colReportedBy: { de: 'Gemeldet von', en: 'Reported by' },
  colSla: { de: 'SLA-Rest', en: 'SLA left' },
  colOpened: { de: 'Eröffnet', en: 'Opened' },
  // ITSM severity words, alongside the P-code rather than replacing it — the
  // code is what an operator radios/searches, the word is what a first-time
  // reader parses without having memorised the scale.
  priorityWordP1: { de: 'Kritisch', en: 'Critical' },
  priorityWordP2: { de: 'Hoch', en: 'High' },
  priorityWordP3: { de: 'Mittel', en: 'Moderate' },
  priorityWordP4: { de: 'Niedrig', en: 'Low' },
  slaBreached: { de: 'SLA verletzt', en: 'SLA breached' },
  majorBadge: { de: 'MAJOR', en: 'MAJOR' },
  suppressionActive: {
    de: '{count} aktive Change-Fenster — Auto-Incidents dort unterdrückt („orange, nicht rot"): {changes}',
    en: '{count} active change window(s) — auto-incidents suppressed there ("orange, not red"): {changes}',
  },
  suppressionAsset: { de: 'Asset', en: 'asset' },

  impact: { de: 'Impact', en: 'Impact' },
  colLocation: { de: 'Standort', en: 'Location' },
  workLogTitle: { de: 'Aktivität', en: 'Activity' },
  appendOnlyTag: { de: 'nur anhängend', en: 'append-only' },
  // R-244 follow-up (user-directed 2026-09-21): `incidentEventLabel`'s
  // dictionary — one line per event `kind` in the backend's closed
  // vocabulary (`incident_events_kind_check`), so the Activity tab reads as
  // sentences instead of raw `kind`+JSON-payload dumps.
  ev_created: { de: 'Angelegt', en: 'Created' },
  ev_reported: { de: 'Gemeldet — SLA-Uhr gestartet', en: 'Reported — the SLA clock started' },
  ev_status: { de: 'Status geändert zu {to}', en: 'Status changed to {to}' },
  ev_statusPlain: { de: 'Status geändert', en: 'Status changed' },
  ev_assigned: { de: 'Zuweisung geändert', en: 'Assignee changed' },
  ev_ack: { de: 'Bestätigt', en: 'Acknowledged' },
  ev_onHold: { de: 'Auf Halten gesetzt', en: 'Put on hold' },
  ev_impactOverride: {
    de: 'Impact manuell vom Klassenstandard abweichend gesetzt',
    en: 'Impact overridden from the class default',
  },
  ev_escalated: { de: 'Eskaliert', en: 'Escalated' },
  ev_slaTier: { de: 'SLA-Eskalationsstufe erreicht', en: 'SLA escalation tier reached' },
  ev_changeLinked: { de: 'Mit einem Change verknüpft', en: 'Linked to a change' },
  ev_childLinked: { de: 'Als verwandter Incident verknüpft', en: 'Linked as a related incident' },
  ev_unassignedAlert: {
    de: 'Markiert — zu lange nicht zugewiesen',
    en: 'Flagged — unassigned too long',
  },
  ev_majorSuggested: {
    de: 'Als möglicher Major Incident markiert',
    en: 'Flagged as a possible major incident',
  },
  ev_majorDeclared: { de: 'Zum Major Incident erklärt', en: 'Declared a major incident' },
  ev_commsDue: { de: 'Stakeholder-Update fällig', en: 'A stakeholder update is due' },
  ev_comms: { de: 'Stakeholder-Update gesendet', en: 'Stakeholder update sent' },
  ev_comment: { de: 'Kommentar hinzugefügt', en: 'Comment added' },
  ev_problemLinked: { de: 'Mit einem Known Error verknüpft', en: 'Linked to a known error' },
  ev_problemUnlinked: { de: 'Known-Error-Verknüpfung entfernt', en: 'Known-error link removed' },
  ev_kbAttached: { de: 'Wissensdatenbank-Artikel angehängt', en: 'Knowledge-base article attached' },
  ev_swap: { de: 'Hardware-Tausch erfasst', en: 'Hardware swap recorded' },
  // `{time}` is a DURATION ("3d", "6h"), never an instant — see the note on
  // `slaWithin`. Auto-close is always ahead of now until it fires.
  confirmBy: { de: 'Auto-Abschluss in {time}', en: 'Auto-close in {time}' },
  confirmOverdue: { de: 'Auto-Abschluss fällig', en: 'Auto-close due' },
  confirmByLabel: { de: 'Auto-Abschluss', en: 'Auto-close' },

  ack: { de: 'Bestätigen', en: 'Acknowledge' },
  startWork: { de: 'Bearbeitung starten', en: 'Start work' },
  hold: { de: 'Anhalten', en: 'Put on hold' },
  resolve: { de: 'Lösen', en: 'Resolve' },
  confirmClose: { de: 'Abschluss bestätigen', en: 'Confirm & close' },
  causeLabel: { de: 'Ursache', en: 'Cause' },
  causePh: { de: 'Was ist ausgefallen / passiert?', en: 'What failed / happened?' },
  actionLabel: { de: 'Maßnahme', en: 'Action' },
  actionPh: { de: 'Was wurde getan, um es zu beheben?', en: 'What was done to fix it?' },
  comment: { de: 'Kommentieren', en: 'Comment' },
  commentPh: { de: 'Arbeitsnotiz…', en: 'Work note…' },

  // The prototype names the ACT, not the noun: "+ Report an incident". That is
  // the whole point of R-244's draft — pressing this writes a row nobody else can
  // see, and REPORTING it later is the step that starts the clock. "New incident"
  // reads as if the record itself were the deliverable.
  report: { de: 'Incident melden', en: 'Report an incident' },
  readOnlyHint: {
    de: 'Nur-Lese-Zugriff — zum Melden oder Bearbeiten von Incidents wird die Berechtigung „Wartung: Schreiben“ benötigt.',
    en: 'Read-only access — raising or managing incidents needs the Maintenance: Write permission.',
  },
  reportSubmit: { de: 'Melden', en: 'Report' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },
  fieldTitle: { de: 'Titel', en: 'Title' },
  titlePh: {
    de: 'z. B. Sensor meldet seit heute Nacht nichts mehr',
    en: 'e.g. Sensor stopped reporting overnight',
  },
  fieldDescription: { de: 'Beschreibung', en: 'Description' },
  descriptionPh: {
    de: 'Was passiert, seit wann, und was bereits versucht wurde',
    en: 'What is happening, since when, and what you already tried',
  },
  fieldImpact: { de: 'Impact', en: 'Impact' },
  fieldUrgency: { de: 'Dringlichkeit', en: 'Urgency' },
  fieldAsset: { de: 'Betroffenes Asset', en: 'Affected asset' },
  incPickAsset: { de: 'Einheit wählen…', en: 'Pick the unit…' },
  waitingOnYou: { de: 'Wartet auf dich', en: 'Waiting on you' },
  yourTurn: { de: 'Du bist dran', en: 'Your turn' },
  p1Open: { de: '{count} P1 offen', en: '{count} P1 open' },
  // R-238 §5 M13 — the number is what makes a P1 urgent rather than merely red.
  p1RespondWithin: { de: '— Reaktion innerhalb von {mins} Minuten', en: '— respond within {mins} minutes' },
  // §5 M9 — which unit the fault is about, and where it is.
  // §5 M12 — the incident's phase line (on_hold is a pause, not a step).
  incStepperLabel: { de: 'Bearbeitungsstand', en: 'Incident progress' },
  // R-244 — the draft phase. V8's stepper opens on "Report", which is the phase
  // BEFORE the clock starts; our `new` is its "Triage".
  // The status CHIP's words — not the stepper's phase names above ("Report" is a
  // step, "Draft" is a state), and the chip used to print the raw enum.
  assignAfterReport: {
    de: 'Zuweisen ist möglich, sobald der Incident gemeldet ist.',
    en: "Assign once it's reported.",
  },
  status_draft: { de: 'Entwurf', en: 'Draft' },
  status_new: { de: 'Neu', en: 'New' },
  status_assigned: { de: 'Zugewiesen', en: 'Assigned' },
  status_in_progress: { de: 'In Arbeit', en: 'In progress' },
  status_on_hold: { de: 'Angehalten', en: 'On hold' },
  status_resolved: { de: 'Behoben', en: 'Resolved' },
  status_closed: { de: 'Abgeschlossen', en: 'Closed' },
  status_cancelled: { de: 'Storniert', en: 'Cancelled' },
  incStep_draft: { de: 'Erfassen', en: 'Report' },
  incStep_new: { de: 'Gemeldet', en: 'Reported' },
  incStep_assigned: { de: 'Zugewiesen', en: 'Assigned' },
  incStep_in_progress: { de: 'In Arbeit', en: 'In progress' },
  incStep_resolved: { de: 'Behoben', en: 'Resolved' },
  incStep_closed: { de: 'Abgeschlossen', en: 'Closed' },

  // ── R-244: the draft phase ────────────────────────────────────────────────
  draftReportIt: { de: 'Melden', en: 'Report it' },
  // R-244 follow-up (user-directed 2026-09-21): why the button is disabled —
  // shown as a native tooltip rather than inline text, since it only matters
  // while the gap exists and the fields it names are already on screen.
  draftReportMissing: {
    de: 'Wählen Sie zuerst die betroffene Anlage, und füllen Sie Titel und Beschreibung aus.',
    en: 'Pick the affected asset first, and fill in the title and description.',
  },
  draftDiscard: { de: 'Verwerfen', en: 'Discard' },
  draftChipEditable: { de: 'bearbeitbar', en: 'editable' },
  // R-244 follow-up (user-directed 2026-09-21): row-level authorization
  // tooltips — the backend restricts these transitions to the assignee (or
  // an approver) and to the reporter (or an approver) respectively
  // (`authorize_incident_write`, gateway-local's `site_itsm_view.rs`).
  onlyAssigneeNote: {
    de: 'Nur die zugewiesene Person (oder ein Approver) kann diese Aktion ausführen.',
    en: 'Only the assignee (or an approver) can do this.',
  },
  onlyReporterNote: {
    de: 'Nur die meldende Person (oder ein Approver) kann diese Aktion ausführen.',
    en: 'Only the reporter (or an approver) can do this.',
  },
  draftPanel: { de: 'Der Fehler', en: 'The fault' },
  // R-235 follow-up: redesign parity — who reported it, who holds it now, and
  // when, live inside "The fault" card as plain Field rows rather than a
  // separate Overview card repeating the same facts.
  reportedByLabel: { de: 'Gemeldet von', en: 'Reported by' },
  reportedAtLabel: { de: 'Gemeldet', en: 'Reported' },
  notYetReported: {
    de: 'noch nicht — die Uhr läuft erst nach dem Melden',
    en: 'not yet — the clock starts when you press Report it',
  },
  assignedToLabel: { de: 'Zugewiesen an', en: 'Assigned to' },
  // The "Change" card's always-on tag and empty state — a linked change is the
  // exception, not the rule, and the card should say so rather than vanish.
  changeNoAssetState: {
    de: 'der Incident schreibt selbst keinen Asset-Status',
    en: 'the incident writes no asset state itself',
  },
  noChangeLinked: {
    de: 'Kein Change ist mit diesem Incident verknüpft.',
    en: 'No change is linked to this incident.',
  },
  draftNote: {
    de: 'Alles hier ist bearbeitbar, solange dies ein Entwurf ist, und wird beim Verlassen jedes Feldes gespeichert — es gibt keinen Speichern-Schritt. Niemand sonst sieht ihn und es läuft noch keine Uhr. Melden startet die SLA-Uhr, stellt ihn in die Triage-Warteschlange und sperrt diese Zeilen.',
    en: 'Everything here is editable while this is a draft, and writes itself as you leave each field — there is no Save step. Nobody else can see it and no clock is running yet. Report it starts the SLA clock, puts it in the triage queue, and locks these rows.',
  },
  draftLockedNote: {
    de: 'Dies sind dieselben Eingabefelder, die der Melder ausgefüllt hat — deaktiviert, nicht durch Text ersetzt. Wer den Incident übernimmt, liest genau dieselbe Seite, und beim Melden hat sich nichts verschoben.',
    en: 'These are the same controls the caller filled in, disabled rather than replaced with text — so whoever picks this up is reading the identical page, and nothing shifted position when it was reported.',
  },
  draftNoClock: {
    de: 'Es läuft noch nichts. Die Ziele folgen aus der Priorität, und die Uhr startet, sobald dies gemeldet wird.',
    en: 'Nothing is running yet. The targets follow from the priority, and the clock starts the moment this is reported.',
  },
  draftDiscardConfirm: {
    de: 'Dies wurde nie gemeldet, es lief keine Uhr und niemand wurde gebeten, es anzusehen. Ein leerer Entwurf wird gelöscht; ein ausgefüllter wird als abgebrochen behalten — das Register löscht keine Datensätze.',
    en: 'This was never reported, so no clock ever started and nobody was ever asked to look at it. A blank draft is deleted; a filled-in one is kept as cancelled — the register does not delete records.',
  },

  // ── R-244: cancellation ───────────────────────────────────────────────────
  cancelIncident: { de: 'Incident abbrechen', en: 'Cancel incident' },
  cancelReasonLabel: { de: 'Warum', en: 'Why' },
  cancelReasonPh: {
    de: 'z. B. Duplikat von INC-0002; kein Fehler',
    en: 'e.g. duplicate of INC-0002; not a fault',
  },
  cancelNote: {
    de: 'Abgebrochen, nicht behoben — es wurde nichts repariert, und der Datensatz sollte nichts anderes behaupten. Der Incident bleibt erhalten: es ist ein Zustand, keine Löschung. Die SLA-Uhr wird gestoppt, ohne Wertung.',
    en: 'Cancelled, not resolved — nothing was fixed, and the record should not claim otherwise. The incident is kept; it is a state, not a deletion. The SLA clock stops with no verdict either way.',
  },
  cancelledBanner: {
    de: 'Abgebrochen. Es wurde nichts repariert, deshalb ist dies nicht behoben.',
    en: 'Cancelled. Nothing was fixed, which is why this is not resolved.',
  },
  slaStopped: { de: 'Uhr gestoppt', en: 'clock stopped' },

  // ── R-244: the record PAGE ────────────────────────────────────────────────
  draftUntitled: { de: 'Neuer Incident', en: 'New incident' },
  recordSubtitle: { de: 'Gemeldet von {caller} · {when}', en: 'Reported by {caller} · {when}' },
  backToIncidents: { de: '← Alle Incidents', en: '← All incidents' },
  recordMissingTitle: { de: 'Incident nicht gefunden', en: 'Incident not found' },
  recordMissingMsg: {
    de: 'Dieser Datensatz existiert nicht mehr — ein leerer Entwurf wird beim Verwerfen gelöscht. Zurück zur Liste.',
    en: 'That record no longer exists — a blank draft is deleted when it is discarded. Back to the list.',
  },
  priorityComputedFrom: {
    de: '· berechnet aus Auswirkung {impact} × Dringlichkeit {urgency}',
    en: '· computed from impact {impact} × urgency {urgency}',
  },
  // §5 M14 — the linked change's execution progress, and the careful zero case.
  halvesConfirmedOf: { de: '{done} von {total} Hälften bestätigt', en: '{done} of {total} halves confirmed' },
  halvesNotScheduledYet: {
    de: 'noch nicht eingeplant — die Freigabe erzeugt die Hälften und reserviert das Ersatzteil',
    en: 'not scheduled yet — approval is what creates the halves and reserves the spare',
  },
  // §5 M6 — the hold note, which the server now requires.
  holdNoteLabel: { de: 'Worauf wird gewartet', en: 'What is being waited for' },
  holdNoteHint: {
    de: 'Der Grund ist die Kategorie; dies ist der Satz, der sagt, wann die Wartezeit endet. „Wartet auf Lieferant" allein sagt niemandem, wann es weitergeht.',
    en: 'The reason is the category; this is the sentence that says when the wait ends. “Awaiting vendor” alone tells nobody when it resumes.',
  },
  holdNotePh: {
    de: 'z. B. Kompressor bestellt, Liefertermin Dienstag',
    en: 'e.g. compressor ordered, due Tuesday',
  },
  // §5 M8 — the failed-change signal.
  causedByChange: {
    de: 'Verursacht durch {number}. Diese Störung trat auf, nachdem diese Änderung ausgeführt wurde.',
    en: 'Caused by {number}. This fault appeared after that change was executed.',
  },
  // §6 B1 — impact derived from the unit, and said out loud.
  impactFromUnit: {
    de: 'Aus der Kritikalität der Einheit ({crit}) vorbelegt. Ändern Sie es nur, wenn es für genau diese Einheit nicht zutrifft.',
    en: 'Defaulted from this unit’s criticality ({crit}). Change it only if that is wrong for this particular unit.',
  },
  // §6 B3 — the pair the prototype shows, because one without the other cannot
  // tell "ignored" from "blocked".
  slaOnClock: { de: '{mins}m auf der Uhr', en: '{mins}m on the clock' },
  slaPausedSoFar: { de: '{mins}m pausiert', en: '{mins}m paused so far' },
  // §6 C7 — the way back when a fix did not hold.
  reopen: { de: 'Nicht behoben — erneut öffnen', en: 'Not fixed — reopen' },
  reopenTitle: { de: 'Störung erneut öffnen', en: 'Reopen this incident' },
  reopenWhy: { de: 'Was ist weiterhin falsch', en: 'What is still wrong' },
  reopenHint: {
    de: 'Die bisherige Lösung bleibt am Datensatz — ein Versuch, der nicht gehalten hat, ist eine Information. Die Uhr läuft dort weiter, wo sie stehen geblieben ist.',
    en: 'The resolution is kept on the record rather than erased — a fix that did not work is information. The clock starts running again from where it stopped.',
  },
  reopenPlaceholder: {
    de: 'z. B. bläst nach zwei Stunden wieder warm',
    en: 'e.g. blowing warm again after two hours',
  },
  // §5 M15 — the frozen diagnosis, shown beside the change it was raised with.
  diagnosisAtTheTime: { de: 'Diagnose zu diesem Zeitpunkt', en: 'Diagnosis at the time' },
  diagnosisFrozen: {
    de: 'Festgehalten, als die Änderung erstellt wurde, und seitdem unverändert — der Kenntnisstand von damals, kein laufender Kommentar.',
    en: 'Captured when the change was raised, and left alone afterwards — it is what was understood then, not a running commentary.',
  },
  colAsset: { de: 'Anlage', en: 'Asset' },
  colWhere: { de: 'Wo', en: 'Where' },
  colAssignee: { de: 'Zugewiesen an', en: 'Assigned to' },
  assetNone: { de: 'raumbezogen', en: 'room-scoped' },
  unassigned: { de: 'nicht zugewiesen', en: 'unassigned' },
  p1Note: {
    de: 'P1 — höchste Auswirkung und höchste Dringlichkeit. Erstreaktion innerhalb von {respond}, Lösung innerhalb von {resolve}.',
    en: 'P1 — highest impact and highest urgency. Respond within {respond}, resolve within {resolve}.',
  },
  onHoldNote: {
    de: 'Pausiert — {reason}. Die SLA-Uhr steht: diese Störung ist blockiert, nicht vernachlässigt.',
    en: 'On hold — {reason}. The SLA clock is paused: this incident is blocked, not neglected.',
  },
  onHoldChangeNote: {
    de: 'Sie läuft von selbst weiter, sobald {number} abgeschlossen ist.',
    en: 'It resumes by itself once {number} settles.',
  },
  resolvedNotClosedNote: {
    de: 'Gelöst, nicht geschlossen. Die meldende Person bestätigt, dass der Betrieb wirklich wiederhergestellt ist — wer die Arbeit gemacht hat, zeichnet die eigene Reparatur nicht ab.',
    en: 'Resolved, not closed. The reporter confirms service is actually restored — the person who did the work does not sign off their own fix.',
  },
  sameHandsNote: {
    de: 'Gemeldet, gelöst und geschlossen von derselben Person. Zulässig — aber hier festgehalten statt versteckt, weil bei der Bestätigung kein zweites Augenpaar beteiligt war.',
    en: 'Reported, resolved and closed by the same person. Allowed — but recorded here rather than hidden, because the confirmation step did not involve a second pair of eyes.',
  },
  unitRightNow: { de: 'Die Einheit gerade jetzt', en: 'The unit right now' },
  liveFromRegister: { de: 'live aus dem Register', en: 'live from the register' },
  unitLoadError: {
    de: 'Der Live-Stand der Einheit konnte nicht geladen werden — die Felder im Ticket beschreiben den Zeitpunkt der Meldung, nicht den jetzigen.',
    en: 'Could not read the unit live — the fields on the ticket describe when it was reported, not now.',
  },
  unitNoLongerInService: { de: 'nicht mehr in Betrieb', en: 'no longer in service' },
  colState: { de: 'Zustand', en: 'State' },
  incAssetHint: {
    de: 'Eine Störung betrifft immer genau eine Einheit, die bereits läuft. Klasse, Modell, Standort und Kosten kommen aus dem Register — sie werden nicht ins Ticket getippt.',
    en: 'An incident is always about exactly one unit that is already running. Class, model, location and cost come off the register rather than being typed into the ticket.',
  },
  incNoneInService: {
    de: 'Keine Einheit ist derzeit in Betrieb — gegen Lagerbestand kann keine Störung gemeldet werden.',
    en: 'No unit is currently in service — an incident cannot be raised against stock.',
  },
  incAssetsUnavailable: {
    de: 'Das Register konnte nicht geladen werden — das ist keine Aussage darüber, was in Betrieb ist. Bitte erneut versuchen.',
    en: 'The register could not be loaded — that is not a statement about what is in service. Please retry.',
  },
  fieldLocation: { de: 'Standort', en: 'Location' },
  selectLocation: { de: '— Standort wählen —', en: '— select location —' },
  fieldGroup: { de: 'Zuständige Gruppe', en: 'Assignment group' },
  priorityHint: {
    de: 'Priorität wird aus Impact × Dringlichkeit berechnet — nie gewählt.',
    en: 'Priority is computed from impact × urgency — never chosen.',
  },

  emptyTitle: { de: 'Keine Incidents', en: 'No incidents' },
  emptyMsg: { de: 'Aktuell ist nichts offen — gut so.', en: 'Nothing open right now — good.' },
  emptyMsgAll: {
    de: 'An diesem Standort wurde noch kein Incident erfasst.',
    en: 'No incident has been recorded at this site yet.',
  },
  loadError: { de: 'Incidents konnten nicht geladen werden', en: 'Failed to load incidents' },

  // R-140 — SLA tiers, hold reasons, resolution codes, TL actions.
  slaTier1: { de: 'SLA 50 %', en: 'SLA 50%' },
  slaTier2: { de: 'SLA 75 %', en: 'SLA 75%' },
  slaTier3: { de: 'SLA 90 %', en: 'SLA 90%' },
  slaPaused: { de: 'SLA pausiert', en: 'SLA paused' },
  autoAssign: { de: 'Auto-zuweisen', en: 'Auto-assign' },
  holdTitle: { de: 'Anhalten — SLA pausiert', en: 'Put on hold — SLA pauses' },
  holdReasonLabel: { de: 'Grund', en: 'Reason' },
  holdReason_awaiting_vendor: { de: 'Wartet auf Lieferant', en: 'Awaiting vendor' },
  holdReason_awaiting_parts: { de: 'Wartet auf Ersatzteile', en: 'Awaiting parts' },
  holdReason_awaiting_caller: { de: 'Wartet auf Melder', en: 'Awaiting caller' },
  holdReason_scheduled: { de: 'Terminiert', en: 'Scheduled' },
  resCodeLabel: { de: 'Lösungscode', en: 'Resolution code' },
  resCodePh: { de: '— Code wählen —', en: '— select code —' },
  // R-244 follow-up (user-directed 2026-09-21): mirrors the server's own
  // refusal message (`CODES_NEEDING_A_CHANGE`, gateway-local's
  // `site_itsm_view.rs`) as a pre-emptive form hint instead of a post-submit
  // error — this code claims hardware moved, and only a CLOSED change (not
  // merely a linked one) may back that claim.
  resolveNeedsChangeHint: {
    de: 'Dieser Code besagt, dass Hardware bewegt wurde — dafür ist ein verknüpfter, ABGESCHLOSSENER Change nötig.',
    en: 'This code claims hardware moved — it needs a linked, CLOSED change to back that up.',
  },
  resCode_workaround_applied: { de: 'Workaround angewendet', en: 'Workaround applied' },
  resCode_hardware_replaced: { de: 'Hardware getauscht', en: 'Hardware replaced' },
  // R-244 — repaired, not replaced: the SAME unit came back (a repair swap).
  // Gated like `hardware_replaced`, because it still claims the register moved.
  resCode_hardware_repaired: { de: 'Hardware repariert', en: 'Hardware repaired' },
  resCode_software_fix: { de: 'Software-Korrektur', en: 'Software fix' },
  resCode_config_change: { de: 'Konfiguration geändert', en: 'Configuration change' },
  resCode_no_fault_found: { de: 'Kein Fehler gefunden', en: 'No fault found' },
  resCode_duplicate: { de: 'Duplikat', en: 'Duplicate' },
  resCode_known_error: { de: 'Bekannter Fehler', en: 'Known error' },
  resCode_vendor_resolved: { de: 'Durch Lieferant gelöst', en: 'Vendor resolved' },
  resCode_caller_withdrew: { de: 'Vom Melder zurückgezogen', en: 'Caller withdrew' },
  majorSuggested: {
    de: 'Korrelierte Störungen erkannt — Major Incident vorgeschlagen.',
    en: 'Correlated incidents detected — major incident suggested.',
  },
  declareMajor: { de: 'Major Incident erklären', en: 'Declare major incident' },
  resolveChildren: { de: 'Offene Kind-Incidents mitlösen', en: 'Resolve open children' },
  commsUpdate: { de: 'Stakeholder-Update (Comms)', en: 'Stakeholder update (comms)' },

  // R-143 — knowledge panel (KE-first), problem-link, KB attach/feedback, drafts.
  knowledgeTitle: { de: 'Wissen', en: 'Knowledge' },
  knowledgeSearchPh: {
    de: 'Bekannte Fehler + KB durchsuchen…',
    en: 'Search known-errors + KB…',
  },
  knowledgeHint: {
    de: 'Bekannte Fehler zuerst, dann passende KB-Artikel.',
    en: 'Known-errors first, then matching KB articles.',
  },
  knowledgeEmpty: { de: 'Keine Treffer', en: 'No matches' },
  knowledgeLoadError: { de: 'Suche fehlgeschlagen', en: 'Search failed' },
  kindKnownError: { de: 'Bekannter Fehler', en: 'Known-error' },
  kindKbArticle: { de: 'KB-Artikel', en: 'KB article' },
  knownErrorTag: { de: 'BEKANNTER FEHLER', en: 'KNOWN ERROR' },
  rootCauseLabel: { de: 'Ursache', en: 'Root cause' },
  workaroundLabel: { de: 'Workaround', en: 'Workaround' },
  fixChangeLabel: { de: 'Fix-Change', en: 'Fix change' },
  attach: { de: 'Anhängen', en: 'Attach' },
  attached: { de: 'Angehängt', en: 'Attached' },
  helpful: { de: 'Hilfreich', en: 'Helpful' },
  notHelpful: { de: 'Nicht hilfreich', en: 'Not helpful' },
  linkProblem: { de: 'Problem verknüpfen', en: 'Link problem' },
  linked: { de: 'Verknüpft', en: 'Linked' },
  unlink: { de: 'Verknüpfung lösen', en: 'Unlink' },

  // Known-error banner on an incident with a linked KE.
  keBannerTitle: { de: 'Verknüpfter bekannter Fehler', en: 'Linked known-error' },
  applyWorkaround: { de: 'Workaround anwenden', en: 'Apply workaround' },
  applyWorkaroundHint: {
    de: 'Löst den Incident schnell mit dem Workaround des bekannten Fehlers (Ursache/Maßnahme werden übernommen).',
    en: "Fast-resolves the incident using the known-error's workaround (cause/action prefilled server-side).",
  },

  // Draft-KB drawer (resolved incident → capture a KB article).
  draftKb: { de: 'KB-Artikel entwerfen', en: 'Draft KB article' },
  draftKbTitle: { de: 'KB-Entwurf aus Incident', en: 'Draft KB from incident' },
  draftKbHint: {
    de: 'Vorbefüllt aus dem gelösten Incident — später im Cloud-Lifecycle geprüft & veröffentlicht.',
    en: 'Prefilled from the resolved incident — reviewed & published later in the cloud lifecycle.',
  },
  draftField_title: { de: 'Titel', en: 'Title' },
  draftField_symptoms: { de: 'Symptome', en: 'Symptoms' },
  draftField_cause: { de: 'Ursache', en: 'Cause' },
  draftField_workaround: { de: 'Workaround', en: 'Workaround' },
  draftField_fix: { de: 'Behebung', en: 'Fix' },
  draftField_body: { de: 'Text', en: 'Body' },
  draftSubmit: { de: 'Entwurf speichern', en: 'Save draft' },
  draftSaved: { de: 'Entwurf gespeichert', en: 'Draft saved' },
  // S-11: KB-draft metadata the site can already capture (kb_drafts API).
  draftField_safety: { de: 'Sicherheitshinweise', en: 'Safety notes' },
  draftField_class: { de: 'Anlagenklasse (Code)', en: 'Asset class (code)' },
  draftField_category: { de: 'Kategorie', en: 'Category' },
  draftField_skill: { de: 'Qualifikation', en: 'Skill' },

  // S-2: explicit assignment (assign-to-me + directory picker).
  assignToMe: { de: 'Mir zuweisen', en: 'Assign to me' },
  assignPh: { de: 'Zuweisen an…', en: 'Assign to…' },
  assigneeLabel: { de: 'Zugewiesen an', en: 'Assignee' },

  // K-8 / L-8: linked changes on the incident + raise-change action.
  linkedChangesTitle: { de: 'Verknüpfte Changes', en: 'Linked changes' },
  raisedFromIncident: { de: 'Aus diesem Incident', en: 'Raised from this incident' },
  openChange: { de: 'Change öffnen', en: 'Open change' },
  raiseChange: { de: 'Change erstellen', en: 'Raise change' },

  // E-1: computed-priority preview in the report form (read-only).
  computedPriority: { de: 'Berechnete Priorität', en: 'Computed priority' },

  // K-7: log-on-behalf caller picker on the report form.
  fieldCaller: { de: 'Anrufer (im Auftrag)', en: 'Caller (on behalf)' },
  callerPh: { de: 'Anrufer wählen…', en: 'Select caller…' },

  // S-10 / L-7: incident SLA panel.
  slaPanelTitle: { de: 'Service-Level', en: 'Service level' },
  slaNotReportedYet: { de: 'noch nicht gemeldet', en: 'not reported yet' },
  slaResponse: { de: 'Reaktion', en: 'Response' },
  slaResolution: { de: 'Lösung', en: 'Resolution' },
  slaElapsed: { de: '{pct}% verbraucht', en: '{pct}% elapsed' },
  // ⚠ The prototype states the WINDOW and then what is left of it — "Respond
  // within 15m — 3h 47m left" — never a bare instant. `Due {time}` used to be
  // fed a future timestamp through the past-only relative formatter, so it read
  // "Due Just now" on every clock that had not yet expired.
  slaWithin: { de: 'innerhalb von {dur}', en: 'within {dur}' },
  slaLeftIn: { de: '{dur} übrig', en: '{dur} left' },
  slaOverdueBy: { de: 'überschritten um {dur}', en: 'overdue by {dur}' },
  slaTierBadge: { de: 'Eskalation T{tier}', en: 'Escalation T{tier}' },
  slaMet: { de: 'Eingehalten', en: 'Met' },
  slaBreachedBadge: { de: 'Verletzt', en: 'Breached' },
  slaSchedule: { de: 'Kalender: {name}', en: 'Calendar: {name}' },
  slaStagePaused: { de: 'Pausiert', en: 'Paused' },

  // N-1..N-6: major-incident workbench (Part N).
  miWorkbench: { de: 'Major-Incident-Workbench', en: 'Major-incident workbench' },
  miBusinessImpact: { de: 'Geschäftliche Auswirkung', en: 'Business impact' },
  miBusinessImpactPh: {
    de: 'Betroffene Dienste / Umfang…',
    en: 'Affected services / scope…',
  },
  miMim: { de: 'Major-Incident-Manager', en: 'Major-incident manager' },
  miMimPh: { de: 'MIM wählen…', en: 'Select MIM…' },
  miBridge: { de: 'Bridge-Link', en: 'Bridge link' },
  miBridgePh: { de: 'https://…', en: 'https://…' },
  miOpenBridge: { de: 'Bridge öffnen', en: 'Open bridge' },
  miChildren: { de: 'Untergeordnete Incidents ({count})', en: 'Child incidents ({count})' },
  miNoChildren: { de: 'Keine untergeordneten Incidents.', en: 'No child incidents.' },
  miComms: { de: 'Kommunikations-Takt', en: 'Comms cadence' },
  miCommsLast: { de: 'Letztes Update: {time}', en: 'Last update: {time}' },
  miCommsNone: { de: 'Noch kein Stakeholder-Update.', en: 'No stakeholder update yet.' },
  miCommsDue: { de: 'Nächstes Update fällig ({time})', en: 'Next update due ({time})' },
  miCommsOk: { de: 'Takt eingehalten', en: 'On cadence' },
  miDeclareDetails: { de: 'Major-Incident-Details', en: 'Major-incident details' },
  miDeclareConfirm: { de: 'Als Major deklarieren', en: 'Declare major' },
  miEditNote: {
    de: 'Diese Felder werden bei der Deklaration gesetzt.',
    en: 'These fields are set at declaration time.',
  },

  // Toasts — raised per call site (`shared/write-toast.ts`), so each one can say
  // exactly what happened to which record rather than a generic "updated".
  toastIncidentDraftCreated: { de: 'Entwurf angelegt', en: 'Draft created' },
  toastIncidentReported: { de: '{n} gemeldet', en: '{n} reported' },
  toastIncidentDraftDiscarded: { de: 'Entwurf verworfen', en: 'Draft discarded' },
  toastIncidentAcked: { de: '{n} bestätigt', en: '{n} acknowledged' },
  toastIncidentStarted: { de: 'Arbeit an {n} begonnen', en: 'Work started on {n}' },
  toastIncidentAssignedToMe: { de: '{n} ist Ihnen zugewiesen', en: '{n} is assigned to you' },
  toastIncidentAutoAssigned: { de: '{n} automatisch zugewiesen', en: '{n} auto-assigned' },
  toastIncidentAssigned: { de: '{n} zugewiesen', en: '{n} assigned' },
  toastIncidentOnHold: { de: '{n} angehalten — SLA pausiert', en: '{n} on hold — SLA paused' },
  toastIncidentResolved: { de: '{n} gelöst', en: '{n} resolved' },
  toastIncidentClosed: { de: '{n} geschlossen', en: '{n} closed' },
  toastIncidentReopened: { de: '{n} wieder geöffnet', en: '{n} reopened' },
  toastIncidentCancelled: { de: '{n} storniert', en: '{n} cancelled' },
  toastCommentAdded: { de: 'Kommentar hinzugefügt', en: 'Comment added' },
  toastMajorDeclared: { de: '{n} als Major Incident deklariert', en: '{n} declared a major incident' },
  toastChildrenResolved: {
    de: '{count} untergeordnete Incidents gelöst',
    en: '{count} child incidents resolved',
  },
  resolveChildrenConfirmMsg: {
    de: 'Alle {count} untergeordneten Incidents werden mit der Lösung dieses Major Incidents gelöst. Das lässt sich hier nicht rückgängig machen.',
    en: "All {count} child incidents will be resolved with this major incident's resolution. This can't be undone from here.",
  },
  toastProblemLinked: { de: 'Problem verknüpft', en: 'Problem linked' },
  toastProblemUnlinked: { de: 'Verknüpfung mit Problem entfernt', en: 'Problem unlinked' },
  toastKbAttached: { de: 'Artikel angehängt', en: 'Article attached' },
  toastKbRated: { de: 'Bewertung gespeichert', en: 'Rating saved' },
  toastKbDraftCreated: { de: 'KB-Entwurf angelegt', en: 'KB draft created' },
  applyWorkaroundConfirmMsg: {
    de: '{n} wird mit dem Workaround aus {ke} als gelöst markiert. Der Melder bestätigt danach die Behebung.',
    en: "{n} will be marked resolved using {ke}'s workaround. The reporter then confirms the fix.",
  },
  unlinkConfirmMsg: {
    de: 'Die Verknüpfung zwischen {n} und {ke} wird entfernt.',
    en: 'The link between {n} and {ke} will be removed.',
  },
  toastWorkaroundApplied: {
    de: 'Workaround angewendet — {n} gelöst',
    en: 'Workaround applied — {n} resolved',
  },
} satisfies Namespace;
